import {Button} from '@mui/material';
import AppLink from 'main/core/AppLink';
import {evaluatePath} from 'main/utility/LayoutUtil';
import PropTypes from 'prop-types';
import {useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  UiWiseActionShowAsType,
  UiWiseActionType,
} from 'shared/constants/UiWiseConst';

const WidgetAction = ({
  action,
  data = null,
  disabled = false,
  customLabel = '',
  onClick = null,
  ...rest
}) => {
  const navigate = useNavigate();

  const linkTo = useMemo(
    () => evaluatePath(action.path, data),
    [action.path, data],
  );

  const actionLabel = customLabel || action.label;

  const handleAction = (e) => {
    // Navigation (Handles "New File")
    if (action.actiontype === UiWiseActionType.REDIRECT && linkTo) {
      return navigate(linkTo);
    }

    // Standard actions (Submit, etc.)
    return onClick?.(e);
  };

  const commonProps = {
    color: action.color,
    disabled: disabled,
  };

  // Render as Link
  if (action.showas === UiWiseActionShowAsType.LINK) {
    return (
      <AppLink href={linkTo} {...commonProps} {...rest}>
        {actionLabel}
      </AppLink>
    );
  }

  // Render as Button
  if (action.showas === UiWiseActionShowAsType.BUTTON) {
    const buttonColor = action.color || 'primary';
    const isPrimary = buttonColor === 'primary';

    return (
      <Button
        {...commonProps}
        color={buttonColor}
        variant={action.variant || (isPrimary ? 'contained' : 'outlined')}
        type={
          action.actiontype === UiWiseActionType.SUBMIT ? 'submit' : 'button'
        }
        onClick={handleAction}
        sx={{
          position: 'relative',
          minWidth: 140,
          ml: 2,
          py: 1.5,
          fontSize: '0.8125rem',
          letterSpacing: '1.2px',
          ...rest.sx,
        }}
        {...rest}
      >
        {actionLabel}
      </Button>
    );
  }

  return null;
};

WidgetAction.propTypes = {
  action: PropTypes.shape({
    showas: PropTypes.oneOf(Object.values(UiWiseActionShowAsType)).isRequired,
    actiontype: PropTypes.oneOf(Object.values(UiWiseActionType)).isRequired,
    label: PropTypes.string,
    path: PropTypes.string,
    color: PropTypes.string,
    variant: PropTypes.string,
  }).isRequired,

  // The row data (used by evaluatePath for dynamic URLs)
  data: PropTypes.object,

  // UI State
  disabled: PropTypes.bool,
  customLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  // The handler (e.g., showDialog for CONFIRM actions)
  onClick: PropTypes.func,
};

export default WidgetAction;
